import * as React from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { AspectRatio } from './aspect-ratio'

const GALLERY_RATIO = 5 / 4

/** Thumbnails shown at once; the strip scrolls to keep the active one in view. */
const VISIBLE_THUMBS = 4

export interface PhotoGalleryImage {
	id: string
	url: string
}

export interface PhotoGalleryProps extends React.HTMLAttributes<HTMLDivElement> {
	images?: PhotoGalleryImage[]
}

function PhotoGallery({ images, className, ...props }: PhotoGalleryProps) {
	const [activeIdx, setActiveIdx] = React.useState(0)

	const total = images?.length ?? 0
	// Deleting the last photo would otherwise leave activeIdx past the end and
	// render the empty placeholder instead of a real image.
	const active = total === 0 ? 0 : Math.min(activeIdx, total - 1)
	const hasMultiple = total > 1

	// The strip is a window over the full list rather than its first four
	// entries, so photo five onwards is reachable and the active thumbnail
	// stays visible while the arrows page through.
	const windowStart = Math.max(
		0,
		Math.min(active - Math.floor((VISIBLE_THUMBS - 1) / 2), total - VISIBLE_THUMBS),
	)
	const visible = (images ?? []).slice(windowStart, windowStart + VISIBLE_THUMBS)

	return (
		<div
			data-slot="photo-gallery"
			className={cn('flex flex-col gap-2', className)}
			{...props}
		>
			{/* Main photo */}
			<AspectRatio ratio={GALLERY_RATIO} className="w-full">
				{images?.[active] ? (
					<img
						src={images[active].url}
						alt=""
						className="absolute inset-0 size-full rounded-lg object-cover"
					/>
				) : (
					<div className="absolute inset-0 flex items-center justify-center rounded-lg bg-gradient-to-br from-(--color-muted) to-(--color-border)" />
				)}
			</AspectRatio>

			{/* Thumbnails */}
			<div className="flex items-center">
				<button
					type="button"
					disabled={!hasMultiple}
					onClick={() => setActiveIdx(active > 0 ? active - 1 : total - 1)}
					className="flex w-6 shrink-0 items-center justify-center disabled:opacity-30"
				>
					<ChevronLeftIcon className="size-4 text-(--color-muted-foreground)" />
				</button>
				{/* Fixed columns keep thumbnails the same size when there are fewer
				    than four photos, instead of stretching one across the strip. */}
				<div className="grid flex-1 grid-cols-4 gap-2">
					{visible.map((img, i) => {
						const idx = windowStart + i
						return (
							<button
								key={img.id}
								type="button"
								onClick={() => setActiveIdx(idx)}
								className={cn(
									'min-w-0 overflow-hidden rounded-lg',
									idx === active && 'ring-2 ring-(--color-primary)',
								)}
							>
								<AspectRatio ratio={GALLERY_RATIO}>
									<img src={img.url} alt="" className="absolute inset-0 size-full rounded-lg object-cover" />
								</AspectRatio>
							</button>
						)
					})}
				</div>
				<button
					type="button"
					disabled={!hasMultiple}
					onClick={() => setActiveIdx(active < total - 1 ? active + 1 : 0)}
					className="flex w-6 shrink-0 items-center justify-center disabled:opacity-30"
				>
					<ChevronRightIcon className="size-4 text-(--color-muted-foreground)" />
				</button>
			</div>
		</div>
	)
}

PhotoGallery.displayName = 'PhotoGallery'

export { PhotoGallery }
